/**
 * src/components/cliente/EstadoDeLaAccion.jsx
 * Qué de lo que un botón de la ficha OFRECE hacer lo está haciendo ya alguien solo.
 *
 * 📌 POR QUÉ EXISTE (20-10-2026, Manuel: «hay funcionalidades que no deberían
 * depender de mí como admin y deberían hacerse automáticamente» · «diferencia
 * los botones que replican funciones que se hacen automáticamente»).
 *
 * De las cuatro acciones de la ficha **dos ya corrían solas** —el censo lo hace
 * el reponedor cada noche, la lectura de precios y OCs la hace el planificador—
 * y la ficha no lo decía en ninguna parte, así que el botón parecía la forma
 * normal de hacerlo. Pasó el 15-10-2026: se censó manquehue a mano a las 21:49 y
 * esa noche el reponedor no tuvo nada que censar.
 *
 * ⚠️ **LA DIFERENCIA QUE IMPORTA no es «automático» contra «a mano»: es
 * «alguien lo hace» contra «NADIE lo hace» — y los dos casos pueden salir del
 * MISMO botón.** Si el interruptor del censo automático se apaga, «Censar
 * convenios» pasa de atajo a única manera. Pintar los dos igual sería prometer
 * un automatismo que no está corriendo, que es el §3 en forma de interfaz: la
 * pantalla más tranquilizadora justo encima del único caso roto. Por eso hay
 * CUATRO estados y no dos, y por eso el punto del distintivo no es adorno —
 * separa «hay una máquina detrás» de «no la hay».
 *
 * ⚠️ **Aquí no se escribe ni qué hace cada acción ni cada cuánto corre.** Eso
 * viene entero en `acciones` del endpoint de la ficha, que lo saca del
 * interruptor y del tope del reponedor y del `SchedulerConfig` del tenant.
 * Escrito aquí, el día que cambie el tope la pantalla seguiría diciendo el
 * número de antes y nadie lo notaría (§4). De este fichero solo es el COLOR.
 *
 * ⚠️ Y la **variante del botón sale de este mismo mapa**, no de cada tarjeta:
 * con la regla repetida en los cuatro sitios, el quinto botón que alguien añada
 * la copiaría mal. Un botón rebajado significa exactamente una cosa —«esto ya lo
 * hace alguien, tú estás adelantándolo»— y eso no puede depender de quién lo
 * pintó.
 *
 * 📌 Va ENCIMA del botón, a propósito. Debajo se lee después de pulsar, y
 * entonces ya no sirve para nada.
 */
import { Badge } from '../ui/Badge'
import { fechaHoraCortaCL } from '../../lib/horaChile'

// El vocabulario entero, en un solo sitio.
const ESTADOS = {
  automatica: { texto: 'SE HACE SOLO',  color: 'green',  punto: true,  variante: 'secondary' },
  parada:     { texto: 'NADIE LO HACE', color: 'yellow', punto: true,  variante: 'primary'   },
  manual:     { texto: 'SOLO A MANO',   color: 'gray',   punto: false, variante: 'secondary' },
  lectura:    { texto: 'SOLO LEE',      color: 'blue',   punto: false, variante: 'secondary' },
}

// Ante una naturaleza desconocida se cae a `manual`, que es la lectura
// conservadora: «nadie lo hace solo». Caer a `automatica` haría que una clave
// nueva mal escrita prometiera un automatismo inexistente.
const _estado = (accion) => ESTADOS[accion?.naturaleza] || ESTADOS.manual

/**
 * La variante del botón de una acción.
 * `secondary` = es un atajo de algo que ya corre solo · `primary` = es la única manera.
 */
export function varianteDeAccion(accion) {
  return _estado(accion).variante
}

/**
 * «Diagnóstico → «Censar convenios»» — la ruta de una acción, para los textos que
 * mandan a alguien a pulsarla.
 *
 * ⚠️ Existe por una avería medida: tres textos de `TiendasDelTenant.jsx`
 * mandaban a «Ejecuciones → "Censar convenios"» desde que esa tarjeta se mudó a
 * «Diagnóstico» el 04-09-2026. La pestaña escrita a mano en la prosa no se rompe
 * cuando la tarjeta se muda: se vuelve mentira en silencio, y
 * `tests/test_rotulos_de_pantalla.py` declara que el rótulo correcto en la ruta
 * equivocada es justo lo que él NO caza.
 *
 * Devuelve `null` si la ficha todavía no ha cargado, y quien lo use tiene que
 * OMITIR la frase en ese caso: una ruta a medias es peor que ninguna.
 */
export function rutaDeAccion(accion) {
  if (!accion?.donde || !accion?.rotulo) return null
  return `${accion.donde} → «${accion.rotulo}»`
}


/** El distintivo y los hechos de una acción: quién la hace, cuándo fue la última vez, qué queda. */
export function EstadoDeLaAccion({ accion }) {
  if (!accion) return null
  const e = _estado(accion)
  const ultima = fechaHoraCortaCL(accion.ultima)

  return (
    <div style={{ marginBottom: 'var(--space-3)', maxWidth: '78ch' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap',
                    gap: 'var(--space-2)' }}>
        <Badge color={e.color} dot={e.punto}>{e.texto}</Badge>
        {accion.quien && accion.cada && (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-2)' }}>
            lo hace {accion.quien} {accion.cada}
          </span>
        )}
      </div>

      {/* La fecha no se enseña en una acción de solo lectura: no «corrió» nunca,
          y un «nunca» ahí se leería como un retraso (§3). */}
      {accion.naturaleza !== 'lectura' && (
        <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--text-xs)',
                      color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          Última vez: {ultima || 'nunca'}
        </div>
      )}

      {/* Lo que midió la última vez, cuando la acción tiene algo que medir.
          Campo GENÉRICO y frase ya compuesta por el servicio: la pantalla no
          sabe de convenios ni de colas, y por eso el día que otra acción quiera
          decir lo que midió no hay que tocar nada aquí.
          ⚠️ Y el servicio devuelve `null` cuando NO lo sabe, así que aquí no
          hace falta —ni se debe— inventar un texto de relleno: una frase de
          disculpa por un campo que falta en resúmenes viejos sería ruido, y un
          número inventado sería una mentira (§3). */}
      {accion.ultima_medida && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)',
                      lineHeight: 1.6, color: 'var(--color-text-2)' }}
             data-ultima-medida>
          {accion.ultima_medida}
        </div>
      )}

      {accion.pendiente && (
        <div style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-warn-soft)',
                      border: '1px solid var(--color-warn-line)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
          Pendiente: {accion.pendiente}
        </div>
      )}

      {accion.porque && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)',
                      lineHeight: 1.6, color: 'var(--color-text-3)' }}>
          {accion.porque}
        </div>
      )}
    </div>
  )
}
